import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import { categories, getComicsByCategory, type Comic } from '@/mocks/comics';

function ComicCard({ comic }: { comic: Comic }) {
  return (
    <Link to={`/comic/${comic.id}`} className="group block" data-product-shop>
      <div className="relative overflow-hidden rounded-lg aspect-[3/4] bg-background-200 mb-3">
        <img
          src={comic.coverImage}
          alt={comic.title}
          title={`${comic.title} - 免费在线看漫画全集`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-2 right-2 px-2 py-0.5 bg-black/60 text-white text-xs rounded-md">
          {comic.status}
        </div>
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent pt-8 pb-3 px-3">
          <div className="flex items-center gap-1 text-accent-400">
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-star-fill text-xs"></i>
            </div>
            <span className="text-xs font-medium">{comic.rating}</span>
          </div>
        </div>
      </div>
      <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
        {comic.title}
      </h3>
      <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
      <div className="flex flex-wrap items-center gap-1 mt-1.5">
        {comic.tags.slice(0, 2).map((tag) => (
          <span key={tag} className="px-1.5 py-0.5 bg-accent-100 text-accent-700 text-[10px] rounded">{tag}</span>
        ))}
      </div>
    </Link>
  );
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryComics = useMemo(() => {
    if (!slug) return [];
    const comics = getComicsByCategory(slug);
    switch (sortBy) {
      case 'popular':
        return [...comics].sort((a, b) => b.views - a.views);
      case 'rating':
        return [...comics].sort((a, b) => b.rating - a.rating);
      default:
        return [...comics].sort((a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime());
    }
  }, [slug, sortBy]);

  if (!currentCategory) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-20 text-center">
            <h1 className="font-heading text-2xl text-foreground-900">分类不存在</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 mt-4 inline-block whitespace-nowrap">返回首页</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const catName = currentCategory.name;
  const catSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${catName}漫画 - 九妖漫画网`,
    description: `九妖漫画网${catName}漫画分类，免费在线看${catName}漫画全集，高清无删减资源。`,
    url: `https://www.jiuyaomanhua.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '九妖漫画网',
    },
  };

  return (
    <>
      <SEOHead
        title={`${catName}漫画 - 免费在线看${catName}漫画全集`}
        description={`九妖漫画网${catName}漫画分类页，免费在线看${catName}漫画全集，高清无删减资源。收录${currentCategory.count}部${catName}类型热门漫画，每日更新。`}
        keywords={`${catName}漫画,九妖漫画网,免费在线看,漫画全集,${catName}`}
        canonicalUrl={`https://www.jiuyaomanhua.com/category/${slug}`}
        breadcrumbs={[
          { label: '首页', href: '/' },
          { label: `${catName}漫画`, href: `/category/${slug}` },
        ]}
        schema={catSchema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          {/* Breadcrumb */}
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4">
            <nav className="flex items-center gap-2 text-sm text-foreground-500" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </div>
              <span className="text-foreground-700 font-medium">{catName}漫画</span>
            </nav>
          </div>

          {/* Category Header */}
          <section className="pb-8">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100">
                  <i className={`${currentCategory.icon} text-xl text-accent-600`}></i>
                </div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  {catName}漫画
                </h1>
              </div>
              <p className="text-sm text-foreground-500 ml-[52px]">
                共 {currentCategory.count} 部作品 · 免费在线看{catName}漫画全集
              </p>
            </div>
          </section>

          {/* Category Tabs */}
          <section className="pb-4">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
                <Link
                  to="/category/rexue"
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                    slug === 'rexue' ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  全部
                </Link>
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                      slug === cat.slug
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Sort & Results */}
          <section className="pb-14">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-6">
                <p className="text-sm text-foreground-500">
                  共 <strong className="text-foreground-700">{categoryComics.length}</strong> 部作品
                </p>
                <div className="flex items-center gap-1 bg-background-100 rounded-full px-1 py-1">
                  {[
                    { key: 'latest', label: '最新' },
                    { key: 'popular', label: '最热' },
                    { key: 'rating', label: '评分' },
                  ].map((s) => (
                    <button
                      key={s.key}
                      onClick={() => setSortBy(s.key as typeof sortBy)}
                      className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                        sortBy === s.key
                          ? 'bg-background-50 text-foreground-900'
                          : 'text-foreground-500 hover:text-foreground-700'
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {categoryComics.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
                  {categoryComics.map((comic) => (
                    <ComicCard key={comic.id} comic={comic} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 mb-4">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无漫画作品</p>
                </div>
              )}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}